/**
 * The brand palette, for the callers that cannot read a stylesheet.
 *
 * Google Maps takes `strokeColor` as a string, Chart.js takes
 * `backgroundColor` as a string, and a react-icons glyph is coloured through
 * the `style` prop. None of the three can be handed a SCSS variable, so the
 * colours were written out by hand: `#0f5bd8` appeared six times across the map,
 * the earnings chart and the driver sidenav, and `#da2828`, `#5a5a5a`,
 * `#6c6c6c` and `#ffffff` once or twice each. Changing the brand blue in the
 * stylesheet left every one of them on the old colour.
 *
 * These are the same values as `src/styles/abstracts/_variables.scss`, and
 * `__tests__/tokens.test.ts` parses that file and compares it against this one,
 * so the two cannot drift. Adding a colour to the stylesheet without adding it
 * here is fine; giving the same name two different values is not.
 *
 * Names match the SCSS ones with the leading `$` dropped and the hyphens
 * turned into camel case, which is what the test checks.
 */
export const colours = {
    // Brand
    primaryBlue: "#0f5bd8",
    primaryBlueActive: "#0e53c5",
    primaryBlueHover: "#3f7ce0",
    fadedBlue: "#e7effb",
    lighterBlue: "#5e91e5",

    // Text
    black: "#1d1d1d",
    textBlack: "#2f2f2f",
    bodyText: "#6c6c6c",

    // Greys and surfaces
    grey: "#5a5a5a",
    fadedGrey: "#fdfdfdcc",
    profileGrey: "#f6f6f6",
    footerGrey: "#f1f1f1",
    lighterGrey: "#999999",
    darkBg: "#e9ebf8",
    lightBg: "#e9ebf8",
    bgDefault: "#ffffff",
    white: "#ffffff",
    border: "#c7c7c7",

    // Status
    caution: "#da2828",
    cautionHover: "#da2828",
    cautionActive: "#ae2020",
    warning: "#ecc435",
    warningHover: "#d4b030",
    warningActive: "#bd9d2a",
    success: "#09a603",
    successHover: "#078502",
    successActive: "#089503",
} as const;

export type ColourName = keyof typeof colours;
